import { useTranslate } from "#src/hooks";
import {
  Button,
  DatePicker,
  Flex,
  notification,
  Popover,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useAntTheme } from "hooks/useAntTheme/";
import React, { useState } from "react";

const { Text } = Typography;

interface TimeLimitSelectorCardProps {
  visible: boolean;
  onConfirm?: (timeType: string, customDate?: Date) => void;
  onClose?: () => void;
  children?: React.ReactNode;
  timeLimit: Date | null;
  popup?: boolean;
}

export const TimeLimitSelectorCard: React.FC<TimeLimitSelectorCardProps> = ({
  visible,
  onConfirm,
  onClose,
  children,
  timeLimit,
  popup = true,
}) => {
  const t = useTranslate();
  const { theme, isMobile } = useAntTheme();
  const [selectedTime, setSelectedTime] = useState<string>(
    timeLimit ? "custom" : "24h"
  );
  const [customDate, setCustomDate] = useState<Date | null>(timeLimit);

  const getExpirationDate = (
    timeType: string,
    customDateValue?: Date | null
  ): Date => {
    const now = dayjs();
    switch (timeType) {
      case "24h":
        return now.add(24, "hour").toDate();
      case "7d":
        return now.add(7, "day").toDate();
      case "30d":
        return now.add(30, "day").toDate();
      case "custom":
        return customDateValue ?? now.add(30, "day").toDate();
      default:
        return now.add(24, "hour").toDate();
    }
  };

  const handleTimeChange = (value) => {
    setSelectedTime(value);
    // AUTO CONFIRM FOR NON POP UP (NEW PROJECT PAGE)
    if (!popup) {
      const expirationDate = getExpirationDate(value, customDate);
      if (expirationDate.getTime() >= dayjs().valueOf()) {
        onConfirm?.(value, expirationDate);
      }
    }
  };

  const handleCustomDateChange = (date: dayjs.Dayjs | null) => {
    const dateValue = date ? date.toDate() : null;
    setCustomDate(dateValue);
    // AUTO CONFIRM FOR NON POP UP (NEW PROJECT PAGE)
    if (!popup && selectedTime === "custom" && dateValue) {
      const expirationDate = getExpirationDate(selectedTime, dateValue);
      if (expirationDate.getTime() >= dayjs().valueOf()) {
        onConfirm?.(selectedTime, expirationDate);
      }
    }
  };

  const handleConfirm = () => {
    const expirationDate = getExpirationDate(selectedTime, customDate);
    if (expirationDate.getTime() < dayjs().valueOf()) {
      notification.error({
        message: t(
          "projects.components.PublicationTypeSelector.selectFutureTime"
        ),
      });
      return;
    }
    onConfirm?.(selectedTime, expirationDate);
    onClose?.();
  };

  const timeOptions = [
    {
      value: "24h",
      labelKey: "projects.components.PublicationTypeSelector.time24Hours",
    },
    {
      value: "7d",
      labelKey: "projects.components.PublicationTypeSelector.time7Days",
    },
    {
      value: "30d",
      labelKey: "projects.components.PublicationTypeSelector.time30Days",
    },
    {
      value: "custom",
      labelKey: "projects.components.PublicationTypeSelector.timeCustom",
    },
  ];

  const expirationDate = getExpirationDate(selectedTime, customDate);

  const formattedDate = dayjs(expirationDate).format("LLLL");
  const expireDateTime = t(
    "projects.components.PublicationTypeSelector.linkWillExpireAt",
    {
      date: formattedDate,
    }
  );

  const content = () => {
    return (
      <Space direction="vertical" size="middle" style={{ marginTop: 10 }}>
        <Flex gap={10}>
          {timeOptions.map((option) => (
            <Button
              key={option.value}
              size="small"
              shape="round"
              type={selectedTime === option.value ? "primary" : "default"}
              onClick={() => handleTimeChange(option.value)}
              style={{
                color: theme.colorText,
                padding: isMobile ? "0 10px" : "0 15px",
              }}
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </Flex>
        {selectedTime === "custom" && (
          <DatePicker
            showTime
            placeholder={t(
              "projects.components.PublicationTypeSelector.selectCustomDate"
            )}
            onChange={handleCustomDateChange}
            value={dayjs(expirationDate)}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        )}

        <Text type="secondary" style={{ fontSize: 11 }}>
          {expireDateTime}
        </Text>

        {popup && (
          <Flex justify="end">
            <Button
              type="primary"
              size="small"
              shape="round"
              onClick={handleConfirm}
            >
              {t("projects.components.PublicationTypeSelector.confirm")}
            </Button>
          </Flex>
        )}
      </Space>
    );
  };

  return popup ? (
    <Popover arrow={false} open={visible} placement="bottom" content={content}>
      {children}
    </Popover>
  ) : (
    <div>{content()}</div>
  );
};
